/**
 * Role Definitions Utility
 * Creates standard role definitions for the GRM client
 * Part of Phase 5: Employee Type Implementation
 */

import { API, graphqlOperation } from 'aws-amplify';
import { createRoleDefinition } from 'graphql/mutations';
import { listRoleDefinitions, roleDefinitionsByClient } from 'graphql/queries';

/**
 * Standard role definitions for GRM client
 */
const GRM_ROLES = [
  // ORGLEVEL_2 roles (Area level)
  {
    roleName: 'Area Manager',
    roleCode: 'AM',
    assignmentLevel: 'ORGLEVEL_1',
    category: 'MANAGEMENT',
    sortOrder: 1,
    isActive: true,
    isDefault: false
  },
  {
    roleName: 'Franchise Area Manager',
    roleCode: 'FAM',
    assignmentLevel: 'ORGLEVEL_1',
    category: 'MANAGEMENT',
    sortOrder: 2,
    isActive: true,
    isDefault: false
  },
  // LOCATION_LEVEL roles
  {
    roleName: 'General Manager',
    roleCode: 'GM',
    assignmentLevel: 'LOCATION_LEVEL',
    category: 'MANAGEMENT',
    sortOrder: 1,
    isActive: true,
    isDefault: true // This is the default role for locations
  },
  {
    roleName: 'Franchise General Manager',
    roleCode: 'FGM',
    assignmentLevel: 'LOCATION_LEVEL',
    category: 'MANAGEMENT',
    sortOrder: 2,
    isActive: true,
    isDefault: false
  }
];

/**
 * Create role definitions for a client
 * @param {string} clientId - Client identifier
 * @param {Function} onProgress - Progress callback
 * @returns {Object} Creation results
 */
export async function createRoleDefinitions(clientId, onProgress = () => {}) {
  const results = {
    created: 0,
    skipped: 0,
    errors: [],
    roles: []
  };

  try {
    onProgress('Checking existing role definitions...');
    
    // Check for existing role definitions
    const existingRolesData = await API.graphql(
      graphqlOperation(roleDefinitionsByClient, {
        clientId: clientId
      })
    );
    
    const existingRoles = existingRolesData.data.roleDefinitionsByClient?.items || [];
    const existingCodes = new Set(existingRoles.map(r => r.roleCode));
    
    onProgress(`Found ${existingRoles.length} existing role definitions`);

    // Create each role if it doesn't exist
    for (const roleDefinition of GRM_ROLES) {
      try {
        // Skip if role already exists
        if (existingCodes.has(roleDefinition.roleCode)) {
          console.log(`Role ${roleDefinition.roleCode} already exists for client ${clientId}`);
          results.skipped++;
          continue;
        }

        // Create the role - note: createdBy is required and should be a user ID
        const roleInput = {
          clientId,
          roleName: roleDefinition.roleName,
          roleCode: roleDefinition.roleCode,
          assignmentLevel: roleDefinition.assignmentLevel,
          category: roleDefinition.category,
          sortOrder: roleDefinition.sortOrder,
          isActive: roleDefinition.isActive,
          isDefault: roleDefinition.isDefault || false,
          createdBy: 'system' // Required field - using 'system' as the user ID for migrations
          // Note: id, createdAt, and lastModified are auto-generated by AWS
          // Note: metadata field is optional and excluded for now
        };

        // Log the input for debugging
        console.log(`Creating role ${roleDefinition.roleCode} with input:`, JSON.stringify(roleInput, null, 2));

        const roleResult = await API.graphql(
          graphqlOperation(createRoleDefinition, { input: roleInput })
        );

        results.roles.push(roleResult.data.createRoleDefinition);
        results.created++;

        onProgress(`Created role: ${roleDefinition.roleName} (${roleDefinition.roleCode})`);
        
      } catch (error) {
        console.error(`Error creating role ${roleDefinition.roleCode}:`, error);
        results.errors.push({
          code: roleDefinition.roleCode,
          name: roleDefinition.roleName,
          error: error.message
        });
      }
    }

    onProgress(`Role creation complete: ${results.created} created, ${results.skipped} skipped`);
    return results;
    
  } catch (error) {
    console.error('Fatal error in role creation:', error);
    throw error;
  }
}


/**
 * Get default role for a scope
 * @param {string} clientId - Client identifier
 * @param {string} assignmentLevel - Assignment level (LOCATION_LEVEL, ORGLEVEL_1, ORGLEVEL_2, ORGLEVEL_3)
 * @returns {Object} Default role or null
 */
export async function getDefaultRole(clientId, assignmentLevel) {
  try {
    const rolesData = await API.graphql(
      graphqlOperation(listRoleDefinitions, {
        filter: {
          clientId: { eq: clientId },
          assignmentLevel: { eq: assignmentLevel },
          isDefault: { eq: true }
        }
      })
    );

    const roles = rolesData.data.listRoleDefinitions?.items || [];

    return roles[0] || null;

  } catch (error) {
    console.error('Error fetching default role:', error);
    return null;
  }
}

/**
 * Get role by code
 * @param {string} clientId - Client identifier
 * @param {string} roleCode - Role code
 * @returns {Object} Role or null
 */
export async function getRoleByCode(clientId, roleCode) {
  try {
    const rolesData = await API.graphql(
      graphqlOperation(listRoleDefinitions, {
        filter: { 
          clientId: { eq: clientId },
          roleCode: { eq: roleCode }
        }
      })
    );
    
    return rolesData.data.listRoleDefinitions?.items[0] || null;
    
  } catch (error) {
    console.error(`Error fetching role with code ${roleCode}:`, error);
    return null;
  }
}

/**
 * Check if all required roles exist for a client
 * @param {string} clientId - Client identifier
 * @returns {Object} Result with exists flag and details
 */
export async function checkRequiredRolesExist(clientId) {
  try {
    const requiredRoleCodes = GRM_ROLES.map(r => r.roleCode);
    
    // Fetch all roles for the client
    const rolesData = await API.graphql(
      graphqlOperation(listRoleDefinitions, {
        filter: { clientId: { eq: clientId } },
        limit: 100
      })
    );
    
    const existingRoles = rolesData.data.listRoleDefinitions?.items || [];
    const existingCodes = new Set(existingRoles.map(r => r.roleCode));
    
    // Check which required roles exist
    const missingRoles = requiredRoleCodes.filter(code => !existingCodes.has(code));
    
    return {
      allExist: missingRoles.length === 0,
      totalRequired: requiredRoleCodes.length,
      totalExisting: existingCodes.size,
      missingRoles,
      existingRoles: existingRoles.map(r => ({
        code: r.roleCode,
        name: r.roleName,
        level: r.assignmentLevel,
        category: r.category
      }))
    };
    
  } catch (error) {
    console.error('Error checking required roles:', error);
    return {
      allExist: false,
      error: error.message
    };
  }
}